import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { QualificationStep } from './steps/QualificationStep';
import { ParticulierTypeStep } from './steps/ParticulierTypeStep';
import { ParticulierOffersStep } from './steps/ParticulierOffersStep';
import { DateLieuStep } from './steps/DateLieuStep';
import { ParticulierDetailsStep } from './steps/ParticulierDetailsStep';
import { RecapitulatifStep } from './steps/RecapitulatifStep';
import { ProfessionnelTypeStep } from './steps/ProfessionnelTypeStep';
import { ProfessionnelParticipantsStep } from './steps/ProfessionnelParticipantsStep';
import { ProfessionnelObjectifsStep } from './steps/ProfessionnelObjectifsStep';
import { CoordonneesStep } from './steps/CoordonneesStep';
import { CategoriesPickStep } from './steps/CategoriesPickStep';
import { CategoryOffersStep } from './steps/CategoryOffersStep';
import { RecapCategoryStep } from './steps/RecapCategoryStep';
import { AddMoreCategoriesStep } from './steps/AddMoreCategoriesStep';
import { CategorySummaryStep } from './steps/CategorySummaryStep';
import { QuickActions } from './QuickActions';
import { useAssistantState } from './hooks/useAssistantState';
import { useIsMobile } from '@/hooks/use-mobile';
import { useInteractionOptimization } from '@/hooks/useInteractionOptimization';
import { useMobileAccessibility } from '@/hooks/useMobileAccessibility';
import { sanitizeSimpleHtml } from '@/lib/sanitize';

type AssistantStep =
  | 'qualification'
  | 'particulier-type'
  | 'categories-pick'
  | 'category-offers'
  | 'recap-category'
  | 'add-more-categories'
  | 'category-summary'
  | 'particulier-offers' // Gardé pour compatibilité
  | 'date-lieu'
  | 'particulier-details'
  | 'recapitulatif'
  | 'professionnel-type'
  | 'professionnel-participants'
  | 'professionnel-objectifs'
  | 'coordonnees'
  | 'success';

interface Message {
  id: string;
  sender: 'bot' | 'user';
  content: string;
  timestamp: Date;
  component?: React.ReactNode;
  isInteractive?: boolean;
  isTyping?: boolean;
}

interface ConversationalAssistantProps {
  onClose?: () => void;
}

export const ConversationalAssistant = ({ onClose }: ConversationalAssistantProps) => {
  const [currentStep, setCurrentStep] = useState<AssistantStep>('qualification');
  const [isTyping, setIsTyping] = useState(false);
  const { state, updateState, resetState } = useAssistantState();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  // États pour le système de catégories
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [processedCategories, setProcessedCategories] = useState<string[]>([]);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [categoryOffers, setCategoryOffers] = useState<any[]>([]);
  
  // Optimisations mobiles
  useInteractionOptimization();
  useMobileAccessibility();

  // Messages initialisés sans référence aux handlers
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      sender: 'bot',
      content: "Bienvenue chez Maison Eridia.",
      timestamp: new Date(),
      isInteractive: false
    },
    {
      id: '2',
      sender: 'bot',
      content: "Je vous accompagne dans la conception de votre évènement, en toute confidentialité.",
      timestamp: new Date(Date.now() + 100),
      isInteractive: false
    },
    {
      id: '3',
      sender: 'bot',
      content: "Pour commencer, vous êtes :",
      timestamp: new Date(Date.now() + 200),
      isInteractive: true
    }
  ]);

  const addMessage = useCallback((content: string, sender: 'bot' | 'user', component?: React.ReactNode, isInteractive = false) => {
    // Diviser le contenu par les sauts de ligne pour créer plusieurs messages
    const contentParts = content.split('\n\n').filter(part => part.trim());
    
    if (contentParts.length > 1 && sender === 'bot') {
      // Afficher l'indicateur de frappe
      setIsTyping(true);
      
      // Créer plusieurs messages pour le bot avec animations séquentielles
      contentParts.forEach((part, index) => {
        const delay = index * 600; // rythme sobre, sans latence gimmick
        
        setTimeout(() => {
          // Ajouter un message de frappe temporaire
          const typingId = `typing_${Date.now()}_${index}`;
          setMessages(prev => [...prev, {
            id: typingId,
            sender: 'bot' as const,
            content: '',
            timestamp: new Date(),
            isTyping: true
          }]);
          
          // Après 1 seconde, remplacer par le vrai message
          setTimeout(() => {
            const newMessage: Message = {
              id: `${Date.now()}_${index}`,
              sender,
              content: part.trim(),
              timestamp: new Date(),
              component: index === contentParts.length - 1 ? component : undefined,
              isInteractive: index === contentParts.length - 1 ? isInteractive : false
            };
            
            setMessages(prev => prev.filter(m => m.id !== typingId).concat(newMessage));
            
            // Arrêter l'indicateur de frappe sur le dernier message
            if (index === contentParts.length - 1) {
              setIsTyping(false);
            }
          }, 500); // Temps de "frappe"
        }, delay);
      });
    } else {
      // Message unique avec indicateur de frappe pour le bot
      if (sender === 'bot') {
        setIsTyping(true);
        
        // Ajouter un message de frappe temporaire
        const typingId = `typing_${Date.now()}`;
        setMessages(prev => [...prev, {
          id: typingId,
          sender: 'bot' as const,
          content: '',
          timestamp: new Date(),
          isTyping: true
        }]);
        
        // Après délai, remplacer par le vrai message
        setTimeout(() => {
          const newMessage: Message = {
            id: Date.now().toString(),
            sender,
            content,
            timestamp: new Date(),
            component,
            isInteractive
          };
          
          setMessages(prev => prev.filter(m => m.id !== typingId).concat(newMessage));
          setIsTyping(false);
        }, 1500); // Temps de "frappe"
      } else {
        // Message utilisateur instantané
        const newMessage: Message = {
          id: Date.now().toString(),
          sender,
          content,
          timestamp: new Date(),
          component,
          isInteractive
        };
        
        setTimeout(() => {
          setMessages(prev => [...prev, newMessage]);
        }, 300);
      }
    }
  }, []);

  // Auto-scroll vers le bas quand de nouveaux messages arrivent
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Scroll automatique quand les messages changent
  useEffect(() => {
    const timer = setTimeout(() => {
      // Faire défiler vers la section interactive si elle existe
      const interactiveElement = document.querySelector('[data-interactive="true"]');
      if (interactiveElement) {
        interactiveElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      } else {
        scrollToBottom();
      }
    }, 200); // Délai un peu plus long pour laisser le DOM se mettre à jour
    
    return () => clearTimeout(timer);
  }, [messages, scrollToBottom]);

  const removeInteractiveMessages = useCallback(() => {
    setMessages(prev => prev.map(msg => ({
      ...msg,
      isInteractive: false, // Désactiver l'interactivité des anciens messages
      component: undefined as React.ReactNode | undefined  // Supprimer les composants des anciens messages
    })));
  }, []);

  const resetConversation = useCallback(() => {
    resetState();
    setCurrentStep('qualification');
    setMessages([
      {
        id: '1',
        sender: 'bot',
        content: "Bienvenue chez Maison Eridia.",
        timestamp: new Date(),
        isInteractive: false
      },
      {
        id: '2',
        sender: 'bot',
        content: "Je vous accompagne dans la conception de votre évènement, en toute confidentialité.",
        timestamp: new Date(Date.now() + 100),
        isInteractive: false
      },
      {
        id: '3',
        sender: 'bot',
        content: "Pour commencer, vous êtes :",
        timestamp: new Date(Date.now() + 200),
        isInteractive: true
      }
    ]);
  }, [resetState]);

  // ===== TOUS LES GESTIONNAIRES D'ÉTAPES =====
  const handleQualificationComplete = useCallback((data: { userType: 'particulier' | 'professionnel' | 'collectivite' }) => {
    removeInteractiveMessages();
    const userType = data.userType;
    const echo = userType === 'particulier' ? 'Particulier' : userType === 'professionnel' ? 'Entreprise' : 'Collectivité';
    addMessage(echo, 'user');
    updateState(data);

    if (userType === 'collectivite') {
      // Les collectivités ont un accompagnement dédié (marchés publics, cadre
      // d'achat). L'assistant oriente vers ce formulaire plutôt que de le dupliquer.
      setTimeout(() => {
        addMessage(
          "Pour les projets de collectivités et du secteur public, la Maison propose un accompagnement dédié, adapté à votre cadre d'achat (consultation directe, MAPA, appel d'offres).\n\nJe vous oriente vers votre interlocuteur dédié.",
          'bot',
          <div className="flex flex-col gap-2">
            <button
              onClick={() => { navigate('/evenementiel-collectivites#contact'); onClose?.(); }}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              Accéder à l'espace Collectivités
            </button>
          </div>,
          true
        );
      }, 400);
      return;
    }

    if (userType === 'particulier') {
      setCurrentStep('particulier-type');
      setTimeout(() => {
        addMessage("Avec plaisir. Quel type d'évènement souhaitez-vous concevoir ?", 'bot', undefined, true);
      }, 400);
    } else {
      setCurrentStep('professionnel-type');
      setTimeout(() => {
        addMessage("Très bien. Quel type d'évènement professionnel envisagez-vous ?", 'bot', undefined, true);
      }, 400);
    }
  }, [removeInteractiveMessages, addMessage, updateState, navigate, onClose]);

  const handleParticulierTypeComplete = useCallback((data: { typeEvenement: string }) => {
    removeInteractiveMessages();
    addMessage(data.typeEvenement, 'user');
    updateState(data);
    // La sélection de « gamme » (Essentiel/Confort/Signature) a été retirée :
    // chaque projet est conçu sur-mesure. On passe directement aux catégories.
    setCurrentStep('categories-pick');
    setTimeout(() => {
      const botMessage = `Choisissez les <strong>catégories</strong> de prestations qui vous intéressent pour votre <strong>${data.typeEvenement}</strong> (multi-sélection).`;
      addMessage(botMessage, 'bot', undefined, true);
    }, 500);
  }, [removeInteractiveMessages, addMessage, updateState]);

  const handleOffersComplete = useCallback((data: { selectedOffers: any[] }) => {
    removeInteractiveMessages();
    addMessage(`${data.selectedOffers.length} offre(s) sélectionnée(s)`, 'user');
    updateState(data);
    setCurrentStep('date-lieu');
    setTimeout(() => {
      const botMessage = "Volontiers.\n\nQuand aura lieu votre évènement ?\n\nVous pouvez aussi indiquer une **date alternative** pour plus de souplesse.";
      addMessage(botMessage, 'bot', undefined, true);
    }, 500);
  }, [removeInteractiveMessages, addMessage, updateState]);

  const handleDateLieuComplete = useCallback((data: any) => {
    removeInteractiveMessages();
    addMessage(`Date: ${new Date(data.dateEvenement).toLocaleDateString('fr-FR')}`, 'user');
    updateState(data);
    if (state.userType === 'particulier') {
      setCurrentStep('particulier-details');
      setTimeout(() => {
        const botMessage = "Quelques détails pour affiner notre proposition.\n\nCombien d'invités attendez-vous ?\n\nCette information nous permet de calculer automatiquement les quantités pour les prestations \"par personne\".";
        addMessage(botMessage, 'bot', undefined, true);
      }, 500);
    } else {
      setCurrentStep('professionnel-participants');
      setTimeout(() => {
        const botMessage = 'Combien de participants attendez-vous ?';
        addMessage(botMessage, 'bot', undefined, true);
      }, 500);
    }
  }, [removeInteractiveMessages, addMessage, updateState, state.userType]);

  const handleDetailsComplete = useCallback((data: any) => {
    removeInteractiveMessages();
    addMessage(`${data.nombreInvites} invité(s)`, 'user');
    updateState(data);
    setCurrentStep('recapitulatif');
    setTimeout(() => {
      const botMessage = 'Voici le récapitulatif de votre demande :\n\nVérifiez que tout est correct avant de finaliser.';
      addMessage(botMessage, 'bot', undefined, true);
    }, 500);
  }, [removeInteractiveMessages, addMessage, updateState]);

  const handleTypeComplete = useCallback((data: { typeEvenement: string }) => {
    removeInteractiveMessages();
    addMessage(data.typeEvenement, 'user');
    updateState(data);
    setCurrentStep('date-lieu');
    setTimeout(() => {
      const botMessage = "Volontiers.\n\nQuand aura lieu votre évènement ?";
      addMessage(botMessage, 'bot', undefined, true);
    }, 500);
  }, [removeInteractiveMessages, addMessage, updateState]);

  const handleParticipantsComplete = useCallback((data: any) => {
    removeInteractiveMessages();
    addMessage(`${data.nombreParticipants} participant(s)`, 'user');
    updateState(data);
    setCurrentStep('professionnel-objectifs');
    setTimeout(() => {
      const botMessage = "Quel est l'objectif principal de cet évènement ?";
      addMessage(botMessage, 'bot', undefined, true);
    }, 500);
  }, [removeInteractiveMessages, addMessage, updateState]);

  const handleObjectifsComplete = useCallback((data: { objectif: string }) => {
    removeInteractiveMessages();
    addMessage(data.objectif, 'user');
    updateState(data);
    setCurrentStep('recapitulatif');
    setTimeout(() => {
      const botMessage = 'Voici le récapitulatif de votre demande :\n\nVérifiez que tout est correct avant de finaliser.';
      addMessage(botMessage, 'bot', undefined, true);
    }, 500);
  }, [removeInteractiveMessages, addMessage, updateState]);

  const handleRecapComplete = useCallback(() => {
    removeInteractiveMessages();
    addMessage('Récapitulatif validé', 'user');
    setCurrentStep('coordonnees');
    setTimeout(() => {
      const botMessage = "Pour finaliser, il me faut simplement vos coordonnées.";
      addMessage(botMessage, 'bot', undefined, true);
    }, 500);
  }, [removeInteractiveMessages, addMessage]);

  const handleCoordonneesComplete = useCallback(() => {
    removeInteractiveMessages();
    addMessage('Coordonnées enregistrées', 'user');
    setCurrentStep('success');
    setTimeout(() => {
      addMessage('Merci. Votre demande est bien reçue — un email de confirmation vient de vous être envoyé, et un chef de projet dédié vous recontacte sous 24h.', 'bot');
      setTimeout(() => {
        addMessage('Souhaitez-vous faire une nouvelle demande ?', 'bot', 
          <div className="flex gap-2">
            <button 
              onClick={resetConversation}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Oui, nouvelle demande
            </button>
            <button 
              onClick={() => {
                addMessage('Merci d\'avoir échangé avec la Maison.', 'bot');
              }}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
            >
              Non, merci
            </button>
          </div>, true);
      }, 1000);
    }, 500);
  }, [removeInteractiveMessages, addMessage, resetConversation]);

  // ===== NOUVEAUX HANDLERS POUR LE SYSTÈME DE CATÉGORIES =====
  const handleCategoriesPickComplete = useCallback((data: { selectedCategories: string[] }) => {
    removeInteractiveMessages();
    addMessage(`${data.selectedCategories.length} catégorie(s) sélectionnée(s)`, 'user');
    setSelectedCategories(data.selectedCategories);
    setCurrentCategoryIndex(0);
    setCurrentStep('category-offers');
    setTimeout(() => {
      const currentCategory = data.selectedCategories[0];
      const botMessage = `Voici les offres **${currentCategory}** adaptées à votre **${state.typeEvenement}**.`;
      addMessage(botMessage, 'bot', undefined, true);
    }, 500);
  }, [removeInteractiveMessages, addMessage, state.typeEvenement]);

  const handleCategoryOffersComplete = useCallback((data: { 
    category: string; 
    selectedOffers: any[]; 
    action: 'next' | 'back' 
  }) => {
    removeInteractiveMessages();
    
    if (data.action === 'back') {
      setCurrentStep('categories-pick');
      setTimeout(() => {
        const botMessage = "Choisissez les <strong>catégories</strong> d'offres qui vous intéressent (multi-sélection).";
        addMessage(botMessage, 'bot', undefined, true);
      }, 500);
      return;
    }

    // Mettre à jour les offres sélectionnées
    setCategoryOffers(prev => {
      const filtered = prev.filter(offer => offer.category !== data.category);
      return [...filtered, ...data.selectedOffers];
    });

    addMessage(`${data.selectedOffers.length} offre(s) ajoutée(s) pour ${data.category}`, 'user');
    setCurrentStep('recap-category');
    setTimeout(() => {
      const botMessage = `Récapitulatif de vos choix pour **${data.category}** :`;
      addMessage(botMessage, 'bot', undefined, true);
    }, 500);
  }, [removeInteractiveMessages, addMessage]);

  const handleRecapCategoryComplete = useCallback(() => {
    removeInteractiveMessages();
    
    const nextIndex = currentCategoryIndex + 1;
    if (nextIndex < selectedCategories.length) {
      // Passer à la catégorie suivante
      setCurrentCategoryIndex(nextIndex);
      setCurrentStep('category-offers');
      setTimeout(() => {
        const nextCategory = selectedCategories[nextIndex];
        const botMessage = `Passons maintenant aux offres **${nextCategory}**.`;
        addMessage(botMessage, 'bot', undefined, true);
      }, 500);
    } else {
      // Toutes les catégories sont terminées
      setProcessedCategories([...selectedCategories]);
      setCurrentStep('add-more-categories');
      setTimeout(() => {
        const botMessage = "Souhaitez-vous **ajouter une autre catégorie** d'offres ou passer au récapitulatif ?";
        addMessage(botMessage, 'bot', undefined, true);
      }, 500);
    }
  }, [removeInteractiveMessages, addMessage, currentCategoryIndex, selectedCategories]);

  const handleAddMoreCategoriesComplete = useCallback((action: 'add_more' | 'summary') => {
    removeInteractiveMessages();
    
    if (action === 'add_more') {
      addMessage('Ajouter une autre catégorie', 'user');
      setCurrentStep('categories-pick');
      setTimeout(() => {
        const botMessage = "Choisissez les <strong>nouvelles catégories</strong> d'offres qui vous intéressent.";
        addMessage(botMessage, 'bot', undefined, true);
      }, 500);
    } else {
      addMessage('Passer au récapitulatif', 'user');
      setCurrentStep('category-summary');
      setTimeout(() => {
        const botMessage = "Voici votre sélection, organisée par catégorie :";
        addMessage(botMessage, 'bot', undefined, true);
      }, 500);
    }
  }, [removeInteractiveMessages, addMessage]);

  const handleCategorySummaryComplete = useCallback(() => {
    removeInteractiveMessages();
    addMessage('Sélection validée', 'user');
    updateState({ selectedOffers: categoryOffers });
    setCurrentStep('date-lieu');
    setTimeout(() => {
      const botMessage = "Volontiers.\n\nQuand aura lieu votre évènement ?";
      addMessage(botMessage, 'bot', undefined, true);
    }, 500);
  }, [removeInteractiveMessages, addMessage, updateState, categoryOffers]);

  const handleCategorySummaryModify = useCallback((category: string) => {
    // Retourner à la modification d'une catégorie spécifique
    const categoryIndex = selectedCategories.indexOf(category);
    if (categoryIndex !== -1) {
      setCurrentCategoryIndex(categoryIndex);
      setCurrentStep('category-offers');
      setTimeout(() => {
        const botMessage = `Modification des offres **${category}** :`;
        addMessage(botMessage, 'bot', undefined, true);
      }, 500);
    }
  }, [selectedCategories, addMessage]);

  const handleCategorySummaryAddCategory = useCallback(() => {
    setCurrentStep('categories-pick');
    setTimeout(() => {
      const botMessage = "Choisissez des **catégories supplémentaires** d'offres.";
      addMessage(botMessage, 'bot', undefined, true);
    }, 500);
  }, [addMessage]);

  // ===== RENDU DU COMPOSANT DE L'ÉTAPE ACTUELLE =====
  const renderCurrentStepComponent = useCallback(() => {
    // On ne rend le composant que si le dernier message est interactif
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage?.isInteractive) return null;

    switch (currentStep) {
      case 'qualification':
        return <QualificationStep onComplete={handleQualificationComplete} />;
      case 'particulier-type':
        return <ParticulierTypeStep onComplete={handleParticulierTypeComplete} />;
      case 'categories-pick':
        return <CategoriesPickStep 
          onComplete={handleCategoriesPickComplete}
          alreadySelected={processedCategories}
        />;
      case 'category-offers':
        return <CategoryOffersStep 
          category={selectedCategories[currentCategoryIndex]}
          typeEvenement={state.typeEvenement || ''}
          currentSelection={categoryOffers}
          onComplete={handleCategoryOffersComplete}
        />;
      case 'recap-category':
        return <RecapCategoryStep 
          category={selectedCategories[currentCategoryIndex]}
          selectedOffers={categoryOffers}
          onComplete={handleRecapCategoryComplete}
          onModify={() => setCurrentStep('category-offers')}
          onRemoveOffer={(offerId) => {
            setCategoryOffers(prev => prev.filter(offer => offer.id !== offerId));
          }}
        />;
      case 'add-more-categories':
        return <AddMoreCategoriesStep 
          processedCategories={processedCategories}
          onComplete={handleAddMoreCategoriesComplete}
        />;
      case 'category-summary':
        return <CategorySummaryStep 
          selectedOffers={categoryOffers}
          processedCategories={processedCategories}
          onComplete={handleCategorySummaryComplete}
          onModifyCategory={handleCategorySummaryModify}
          onAddCategory={handleCategorySummaryAddCategory}
        />;
      case 'particulier-offers':
        return <ParticulierOffersStep 
          typeEvenement={state.typeEvenement}
          selectedOffers={state.selectedOffers || []}
          onComplete={handleOffersComplete}
        />;
      case 'date-lieu':
        return <DateLieuStep onComplete={handleDateLieuComplete} />;
      case 'particulier-details':
        return <ParticulierDetailsStep onComplete={handleDetailsComplete} />;
      case 'professionnel-type':
        return <ProfessionnelTypeStep onComplete={handleTypeComplete} />;
      case 'professionnel-participants':
        return <ProfessionnelParticipantsStep onComplete={handleParticipantsComplete} />;
      case 'professionnel-objectifs':
        return <ProfessionnelObjectifsStep onComplete={handleObjectifsComplete} />;
      case 'recapitulatif':
        return <RecapitulatifStep state={state} onComplete={handleRecapComplete} />;
      case 'coordonnees':
        return <CoordonneesStep state={state} onComplete={handleCoordonneesComplete} />;
      default:
        return null;
    }
  }, [messages, currentStep, state, selectedCategories, currentCategoryIndex, categoryOffers, processedCategories, handleQualificationComplete, handleParticulierTypeComplete, handleCategoriesPickComplete, handleCategoryOffersComplete, handleRecapCategoryComplete, handleAddMoreCategoriesComplete, handleCategorySummaryComplete, handleCategorySummaryModify, handleCategorySummaryAddCategory, handleOffersComplete, handleDateLieuComplete, handleDetailsComplete, handleTypeComplete, handleParticipantsComplete, handleObjectifsComplete, handleRecapComplete, handleCoordonneesComplete]);

  return (
    <div className="h-full flex flex-col min-h-0">
      <Card className="h-full flex flex-col border-0 shadow-none min-h-0">
        <div className={`flex items-center justify-between bg-[#0a2540] text-white ${isMobile ? 'px-4 py-3' : 'px-5 py-4'}`}>
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-white/[0.08] ring-1 ring-[#C9A96E]/60 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#C9A96E]" aria-hidden="true" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#0a2540]" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <div className="font-medium leading-tight truncate">Maison Eridia</div>
              <div className="text-xs text-white/60 leading-tight">Conseiller · en ligne</div>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors rounded-full w-8 h-8 flex items-center justify-center hover:bg-white/10 flex-shrink-0"
              aria-label="Fermer l'assistant"
            >
              ✕
            </button>
          )}
        </div>

        <CardContent className="flex-1 flex flex-col p-0 min-h-0">
          {/* Messages Area */}
          <div
            className={`flex-1 min-h-0 bg-[#f7f8fa] ${
              isMobile
                ? 'px-3 pt-4 pb-3 space-y-3 overflow-y-auto scrollbar-hide'
                : 'px-5 pt-5 pb-5 space-y-4 overflow-y-auto scrollbar-hide'
            }`}
            style={{ 
              scrollBehavior: 'smooth',
              WebkitOverflowScrolling: 'touch',
              overflowY: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {messages.map((message, index) => (
              <div
                key={message.id}
                className={`flex animate-fade-in ${
                  message.sender === 'user' ? 'justify-end' : 'justify-start'
                } ${isMobile ? 'gap-2' : 'gap-3'}`}
              >
                {message.sender === 'bot' && (
                  <div className={`rounded-full bg-[#0a2540] flex items-center justify-center flex-shrink-0 self-end mb-1 ${
                    isMobile ? 'w-7 h-7' : 'w-8 h-8'
                  }`}>
                    {message.isTyping ? (
                      <div className="flex space-x-0.5">
                        <div className="w-1 h-1 bg-[#C9A96E] rounded-full animate-pulse"></div>
                        <div className="w-1 h-1 bg-[#C9A96E] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-1 h-1 bg-[#C9A96E] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    ) : (
                      <Sparkles className={`text-[#C9A96E] ${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} aria-hidden="true" />
                    )}
                  </div>
                )}
                
                <div
                  className={`max-w-[85%] transition-all duration-300 ${
                    isMobile ? 'px-3.5 py-2.5' : 'px-4 py-2.5'
                  } ${
                    message.sender === 'user'
                      ? 'bg-[#0a2540] text-white ml-auto rounded-2xl rounded-br-md animate-scale-in shadow-sm'
                      : message.isTyping
                        ? `bg-white text-[#1a2332] rounded-2xl rounded-bl-md border border-black/[0.04] shadow-sm flex items-center ${isMobile ? 'min-h-[36px]' : 'min-h-[40px]'}`
                        : 'bg-white text-[#1a2332] rounded-2xl rounded-bl-md border border-black/[0.04] shadow-sm animate-fade-in'
                  }`}
                  data-interactive={message.sender === 'bot' && message.isInteractive && index === messages.length - 1 ? 'true' : 'false'}
                  style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                >
                  {message.isTyping ? (
                    <div className="flex items-center space-x-2 text-muted-foreground">
                      <div className="flex space-x-1">
                        <div className={`bg-muted-foreground/40 rounded-full animate-bounce ${isMobile ? 'w-1 h-1' : 'w-1.5 h-1.5'}`}></div>
                        <div className={`bg-muted-foreground/40 rounded-full animate-bounce ${isMobile ? 'w-1 h-1' : 'w-1.5 h-1.5'}`} style={{ animationDelay: '0.1s' }}></div>
                        <div className={`bg-muted-foreground/40 rounded-full animate-bounce ${isMobile ? 'w-1 h-1' : 'w-1.5 h-1.5'}`} style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className={isMobile ? 'text-xs' : 'text-xs'}>En train d'écrire...</span>
                    </div>
                  ) : (
                    <div className={`whitespace-pre-line leading-relaxed ${
                      isMobile ? 'text-sm' : 'text-sm'
                    }`} dangerouslySetInnerHTML={{
                      __html: sanitizeSimpleHtml(message.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'))
                    }} />
                  )}
                  
                  {/* Composant interactif SEULEMENT pour le dernier message et si pas en cours de frappe */}
                  {message.sender === 'bot' && message.isInteractive && index === messages.length - 1 && !message.isTyping && (
                    <div className={`animate-scale-in overflow-y-auto scrollbar-thin ${
                      isMobile ? 'mt-3 max-h-60' : 'mt-3 max-h-72'
                    }`}>
                      {renderCurrentStepComponent()}
                    </div>
                  )}
                  
                  {/* Résumé des anciens choix retiré (rendu épuré). */}

                  {/* Component défini dans le message - seulement si interactif ET dernier et pas en cours de frappe */}
                  {message.component && message.isInteractive && index === messages.length - 1 && !message.isTyping && (
                    <div className={`animate-scale-in overflow-y-auto scrollbar-thin ${
                      isMobile ? 'mt-3 max-h-60' : 'mt-3 max-h-72'
                    }`}>
                      {message.component}
                    </div>
                  )}
                  
                  {/* Horodatage retiré (rendu épuré). */}
                </div>

                {/* Avatar utilisateur retiré (rendu épuré). */}
              </div>
            ))}
            
            {/* Élément invisible pour l'auto-scroll */}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions - Plus compact */}
          <div className="flex-shrink-0">
            <QuickActions onClose={onClose} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};